import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  InputGroup,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import { useBlocksConfig } from "@pathwright/blocks-engine"
import { EditIcon } from "@pathwright/pathicons"
import { PRIMARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import { useMemo, useState } from "react"
import validateURL, { getValidationMessage } from "../../utils/urls/validateURL"

const inputGray = "rgb(136, 136, 136)"
const inputFontSize = "0.9em"

export const ButtonBlockButton = ({ link, label, onClick, disabled }) => {
  const { theme } = useBlocksConfig()

  const isDisabled = useMemo(
    () => disabled || (!link && !onClick),
    [disabled, link, onClick]
  )

  const handleClick = (event) => {
    if (onClick || isDisabled) {
      event.preventDefault()
      event.stopPropagation()
      if (!isDisabled) onClick(event)
    }
  }

  const isRelative = link?.startsWith("/")

  return (
    <Button
      as={Link}
      bgColor={theme?.primaryBrandColor || PRIMARY_GRAY}
      _hover={{
        bgColor: theme?.primaryBrandColor || PRIMARY_GRAY,
        opacity: isDisabled ? 0.4 : 0.9
      }}
      fontSize="16px"
      textDecoration="none !important"
      padding="7px 18px"
      margin="9px 0px"
      lineHeight="1.4"
      cursor={!isDisabled ? "pointer !important" : undefined}
      fontFamily="body"
      color="white !important"
      isDisabled={isDisabled}
      target={isRelative ? undefined : "_blank"}
      href={link}
      onClick={handleClick}
    >
      {label || "Join Meeting"}
    </Button>
  )
}

export const ButtonEditButton = ({
  onChange,
  buttonLabel: propsButtonLabel,
  link: propsLink
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [buttonLabel, setButtonLabel] = useState(propsButtonLabel)
  const [link, setButtonLink] = useState(propsLink)
  const [error, setError] = useState("")

  const handleClose = () => {
    setButtonLabel(propsButtonLabel)
    setButtonLink(propsLink)
    onClose()
  }

  const handleChangeButtonLabel = (nextButtonLabel) =>
    setButtonLabel(nextButtonLabel)

  const handleChangeLink = (nextLink) => {
    setButtonLink(nextLink.trim())
    setError("")
  }

  const handleSubmit = () => {
    onChange({ buttonLabel, link: link.trim() })
    onClose()
  }

  const linkValidation = useMemo(() => validateURL(link), [link])
  const linkIsValid = useMemo(() => linkValidation === true, [linkValidation])

  const validateLink = () => {
    if (!linkIsValid) {
      // Automatically supply https:// if no protocol
      if (linkValidation === "missingProtocol") {
        setButtonLink(`https://${link}`)
        return
      }
      const message = getValidationMessage(linkValidation)
      setError(message)
    } else {
      setError("")
    }
  }

  return (
    <Popover onOpen={onOpen} isOpen={isOpen} onClose={handleClose}>
      <PopoverTrigger>
        <HStack w="max-content" pos="relative" justify="center">
          <ButtonBlockButton
            label={buttonLabel}
            link={link}
            onClick={onOpen}
            disabled={!linkIsValid}
          />
          <Box position="absolute" right="-40px">
            <IconButton
              icon={<EditIcon size="1em" />}
              aria-label="Edit button"
              bg="rgb(239, 239, 239)"
              _hover={{
                bg: "rgb(239, 239, 239)",
                opacity: 0.9
              }}
              cursor="pointer"
              color="rgb(136, 134, 135)"
              height="30px"
              width="30px"
              minW="30px"
            />
          </Box>
        </HStack>
      </PopoverTrigger>
      <Portal>
        <PopoverContent borderRadius="md">
          <PopoverArrow />
          <PopoverBody py={10} px={5} bg="white" borderRadius="md">
            <VStack gap={5}>
              <InputGroup>
                <Box
                  as="label"
                  w="full"
                  color={inputGray}
                  fontSize={inputFontSize}
                >
                  Link
                  <Input
                    border="1px solid rgba(0,0,0,0.2)"
                    marginTop="0.5rem"
                    placeholder="https://www.pathwright.com"
                    value={link}
                    borderRadius="4px"
                    p="5px"
                    onChange={(e) => handleChangeLink(e.target.value)}
                    onBlur={validateLink}
                    color={PRIMARY_GRAY}
                    isInvalid={!!error}
                    _placeholder={{ color: "rgba(0,0,0,0.2)" }}
                  />
                  {error && (
                    <Box color="red.500" mt="2" fontSize="sm">
                      {error}
                    </Box>
                  )}
                </Box>
              </InputGroup>
              <InputGroup>
                <Box
                  as="label"
                  w="full"
                  color={inputGray}
                  fontSize={inputFontSize}
                >
                  Label
                  <Input
                    border="1px solid rgba(0,0,0,0.2)"
                    marginTop="0.5rem"
                    placeholder="Label"
                    value={buttonLabel}
                    borderRadius="4px"
                    p="5px"
                    onChange={(e) => handleChangeButtonLabel(e.target.value)}
                    color={PRIMARY_GRAY}
                    _placeholder={{ color: "rgba(0,0,0,0.2)" }}
                  />
                </Box>
              </InputGroup>
              <Button
                onClick={handleSubmit}
                w="full"
                borderRadius="md"
                bg={PRIMARY_GRAY}
                color="white"
                _hover={{ bg: PRIMARY_GRAY }}
                isDisabled={
                  !linkIsValid ||
                  (link === propsLink && buttonLabel === propsButtonLabel)
                }
              >
                Save
              </Button>
              <HStack>
                <Button
                  variant="link"
                  onClick={handleClose}
                  textDecoration="underline"
                  color={PRIMARY_GRAY}
                >
                  Or cancel
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
